import React, { useState, FormEvent, useRef, useEffect, useCallback } from 'react';
import { useStripe, useElements, PaymentElement, Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { API_CONFIG } from '../utils/constants';
import { useTracking } from '../hooks/useTracking';
import { useDeduplicatedPostHog } from '../utils/posthogWrapper';

interface StripeCheckoutFormProps {
  clientSecret: string;
  customerId: string;
  planId: string;
  billingPeriod: string;
  userId?: string;
  onComplete?: () => void;
  onError?: (error: Error) => void;
}

// Inner form component that uses Stripe hooks
function StripeCheckoutForm({ 
  clientSecret, 
  customerId, 
  planId, 
  billingPeriod,
  userId,
  onComplete, 
  onError 
}: StripeCheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { track } = useTracking();
  const posthog = useDeduplicatedPostHog(); // Use deduplicated wrapper
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const propertiesSetRef = useRef(false); // Track if properties were set for this transaction
  const [paymentElementReady, setPaymentElementReady] = useState(false);
  const paymentElementMountTimeRef = useRef<number>(Date.now());

  // Track when PaymentElement successfully mounts
  const handlePaymentElementReady = useCallback(() => {
    const mountDuration = Date.now() - paymentElementMountTimeRef.current;
    setPaymentElementReady(true);
    
    track('payment_element_ready', {
      component: 'EmbeddedCheckout',
      plan_id: planId,
      billing_period: billingPeriod,
      mount_duration_ms: mountDuration,
    });
    
    console.log(`✅ [EmbeddedCheckout] PaymentElement ready in ${mountDuration}ms`);
  }, [track, planId, billingPeriod]);

  // Track PaymentElement errors and completion
  const handlePaymentElementChange = useCallback((event: any) => {
    if (event.error) {
      console.error('❌ [EmbeddedCheckout] PaymentElement error:', event.error);
      
      track('payment_element_error', {
        component: 'EmbeddedCheckout',
        error_type: event.error.type,
        error_code: event.error.code,
        error_message: event.error.message,
        plan_id: planId,
        billing_period: billingPeriod,
      });
    }
    
    if (event.complete) {
      track('payment_element_complete', {
        component: 'EmbeddedCheckout',
        plan_id: planId,
        billing_period: billingPeriod,
      });
    }
  }, [track, planId, billingPeriod]);

  // Timeout detection: if PaymentElement doesn't become ready within 10 seconds, show error
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!paymentElementReady) {
        const waitTime = Date.now() - paymentElementMountTimeRef.current;
        
        console.error(`❌ [EmbeddedCheckout] PaymentElement timeout after ${waitTime}ms`);
        
        track('payment_element_timeout', {
          component: 'EmbeddedCheckout',
          plan_id: planId,
          billing_period: billingPeriod,
          wait_time_ms: waitTime,
          stripe_loaded: !!stripe,
          elements_loaded: !!elements,
        });
        
        setErrorMessage(
          'Payment form is taking longer than expected to load. ' +
          'Please refresh the page and try again. If the problem persists, contact support.'
        );
        
        if (onError) {
          onError(new Error('PaymentElement mount timeout'));
        }
      }
    }, 10000); // 10 second timeout

    return () => clearTimeout(timeoutId);
  }, [paymentElementReady, stripe, elements, track, planId, billingPeriod, onError]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      // Submit payment form to Stripe
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setErrorMessage(submitError.message || 'Payment form validation failed');
        setIsProcessing(false);
        return;
      }

      // Confirm SetupIntent to collect payment method
      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        clientSecret,
        redirect: 'if_required', // Only redirect if required (e.g., 3D Secure)
      });

      if (confirmError) {
        setErrorMessage(confirmError.message || 'Payment method collection failed');
        if (onError) {
          onError(new Error(confirmError.message || 'Payment method collection failed'));
        }
        setIsProcessing(false);
        return;
      }

      if (!setupIntent || !setupIntent.payment_method) {
        throw new Error('Payment method not collected');
      }

      // Payment method collected, now create subscription
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/stripe/create-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customer_id: customerId,
          payment_method_id: setupIntent.payment_method,
          plan_id: planId,
          billing_period: billingPeriod,
          user_id: userId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create subscription');
      }

      const data = await response.json();
      
      if (data.success) {
        // Track subscribe event (subscription purchase)
        // Hardcoded conversion values based on plan type (same for monthly and annual)
        const conversionValuesByPlan: Record<string, number> = {
          'basic': 20,
          'pro': 40,
          'premium': 80,
        };
        const conversionValue = conversionValuesByPlan[planId.toLowerCase()] || 0;
        
        track('subscribe', {
          transaction_id: data.subscription_id || data.transaction_id || `sub_${Date.now()}`,
          plan_id: planId,
          plan_name: planId, // Could be enhanced to get plan name
          currency: 'USD',
          value: conversionValue, // Hardcoded: Basic=$20, Pro=$40, Premium=$80
          billing_period: billingPeriod,
          product_type: 'subscription', // Distinguish from credit packs
          user_id: userId || '',
          timestamp: new Date().toISOString(),
        });

        // Update user properties with payment info (deduplicated via wrapper)
        if (posthog && userId && !propertiesSetRef.current) {
          posthog.setPersonProperties({
            has_payment_method: true,
            subscription_plan: planId,
            subscription_billing_period: billingPeriod,
            subscription_status: 'active',
            last_purchase_date: new Date().toISOString(),
            payment_method_type: 'card',
          });
          propertiesSetRef.current = true; // Mark as set to prevent duplicates in this transaction
        }
        
        // Subscription created successfully
        if (onComplete) {
          onComplete();
        }
      } else {
        throw new Error('Subscription creation failed');
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('An unexpected error occurred');
      setErrorMessage(error.message);
      if (onError) {
        onError(error);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-white/5 border border-white/20 rounded-lg p-4 min-h-[300px] relative">
        {!paymentElementReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/5 rounded-lg">
            <div className="text-center space-y-2">
              <div className="animate-pulse text-white/70">Loading payment form...</div>
              <div className="text-xs text-white/50">Please wait</div>
            </div>
          </div>
        )}
        
        <PaymentElement 
          onReady={handlePaymentElementReady}
          onChange={handlePaymentElementChange}
          options={{
            layout: 'tabs',
          }}
        />
      </div>

      {errorMessage && (
        <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
          {errorMessage}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full bg-[#F9F7EA] text-[#0f1218] px-6 py-3 rounded-lg font-semibold hover:bg-[#E7D7B1] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isProcessing ? 'Processing...' : 'Subscribe Now'}
      </button>
    </form>
  );
}

// Main component that wraps with Elements provider
interface EmbeddedCheckoutProps {
  clientSecret: string;
  customerId: string;
  planId: string;
  billingPeriod: string;
  publishableKey?: string; // Optional: if provided, use this key instead of fetching
  userId?: string;
  onComplete?: () => void;
  onError?: (error: Error) => void;
}

export default function EmbeddedCheckoutComponent({ 
  clientSecret,
  customerId,
  planId,
  billingPeriod,
  publishableKey,
  userId,
  onComplete,
  onError 
}: EmbeddedCheckoutProps) {
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { track } = useTracking();

  useEffect(() => {
    const initializeStripe = async () => {
      const startTime = Date.now();
      let stage = 'started';
      
      try {
        setLoading(true);
        setError(null);

        // Track initialization start
        track('stripe_init_started', {
          component: 'EmbeddedCheckout',
          plan_id: planId,
          billing_period: billingPeriod,
          timestamp: new Date().toISOString(),
        });

        stage = 'fetching_key';
        let key = publishableKey;

        // If no key provided, fetch from backend config endpoint
        if (!key) {
          try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout for API call
            
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/stripe/config`, {
              signal: controller.signal,
            });
            clearTimeout(timeoutId);
            
            const data = await response.json();
            
            if (data.success && data.publishable_key) {
              const fetchedKey = data.publishable_key;
              key = fetchedKey;
              
              track('stripe_key_fetched', { 
                duration_ms: Date.now() - startTime,
              });
            } else {
              console.error('[EmbeddedCheckout] Invalid config response:', data);
              throw new Error('Invalid response from config endpoint');
            }
          } catch (configError) {
            const errorMsg = configError instanceof Error ? configError.message : 'Unknown error';
            console.error('[EmbeddedCheckout] Failed to fetch config:', configError);
            
            track('stripe_key_fetch_failed', { 
              error: errorMsg,
              duration_ms: Date.now() - startTime,
            });
            
            throw new Error('Failed to get Stripe configuration from backend');
          }
        }

        if (!key) {
          throw new Error('Stripe publishable key is required');
        }

        // Verify we're using the correct key type
        stage = 'validating_key';
        const isTestKey = key.startsWith('pk_test_');
        const isLiveKey = key.startsWith('pk_live_');
        const hostname = window.location.hostname;
        const isDevEnvironment = hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('dev') || API_CONFIG.BASE_URL.includes('devapi');
        
        // For localhost/dev, we MUST use test key - REJECT live keys immediately
        if (isDevEnvironment) {
          if (isLiveKey) {
            const errorMsg = 'Live Stripe key detected in development environment. Backend should return test key.';
            console.error('[EmbeddedCheckout]', errorMsg);
            setError('Backend returned incorrect Stripe key for development. Please contact support.');
            throw new Error(errorMsg);
          }
          if (!isTestKey) {
            const errorMsg = `Invalid Stripe key format. Expected pk_test_... but got: ${key.substring(0, 30)}...`;
            console.error('[EmbeddedCheckout]', errorMsg);
            setError('Invalid Stripe configuration. Please contact support.');
            throw new Error(errorMsg);
          }
        }
        
        // Initialize Stripe with timeout protection
        stage = 'loading_stripe_js';
        track('stripe_js_loading_started', { 
          duration_ms: Date.now() - startTime,
        });
        
        const stripeLoadPromise = loadStripe(key);
        const timeoutPromise = new Promise<null>((_, reject) => {
          setTimeout(() => reject(new Error('Stripe.js load timeout after 15 seconds')), 15000);
        });
        
        const stripe = await Promise.race([stripeLoadPromise, timeoutPromise]);
        
        if (!stripe) {
          throw new Error('Stripe.js failed to load (null returned)');
        }
        
        const totalDuration = Date.now() - startTime;
        track('stripe_js_loaded_success', { 
          duration_ms: totalDuration,
          plan_id: planId,
          billing_period: billingPeriod,
        });
        
        setStripePromise(Promise.resolve(stripe));
        
      } catch (err) {
        const duration = Date.now() - startTime;
        const errorMessage = err instanceof Error ? err.message : 'Failed to initialize Stripe';
        
        // Detect specific browser/environment issues
        const browserInfo = {
          user_agent: navigator.userAgent,
          is_facebook_browser: navigator.userAgent.includes('FBAN') || navigator.userAgent.includes('FBAV'),
          is_android: /Android/i.test(navigator.userAgent),
          is_chrome: /Chrome/i.test(navigator.userAgent),
          is_ios: /iPhone|iPad|iPod/i.test(navigator.userAgent),
          is_safari: /Safari/i.test(navigator.userAgent) && !/Chrome/i.test(navigator.userAgent),
          network_type: (navigator as any).connection?.effectiveType || 'unknown',
          downlink: (navigator as any).connection?.downlink || 'unknown',
        };
        
        console.error('❌ [EmbeddedCheckout] Stripe initialization error:', err);
        console.error('❌ [EmbeddedCheckout] Browser info:', browserInfo);
        
        track('stripe_init_failed', {
          stage,
          error: errorMessage,
          duration_ms: duration,
          plan_id: planId,
          billing_period: billingPeriod,
          ...browserInfo,
        });
        
        // User-friendly error messages based on the failure stage
        let userMessage = 'Failed to load payment form. ';
        if (stage === 'loading_stripe_js') {
          userMessage += 'This may be due to a slow network connection. Please refresh the page and try again.';
        } else if (stage === 'fetching_key') {
          userMessage += 'Unable to connect to payment service. Please check your internet connection and try again.';
        } else {
          userMessage += 'Please refresh the page and try again.';
        }
        
        setError(userMessage);
        if (onError) {
          onError(new Error(errorMessage));
        }
      } finally {
        setLoading(false);
      }
    };

    initializeStripe();
  }, [publishableKey, onError, track, planId, billingPeriod]);

  if (loading) {
    return (
      <div className="p-4 bg-white/5 border border-white/20 rounded-lg text-white/70 text-center space-y-2">
        <div className="animate-pulse">Initializing payment form...</div>
        <div className="text-xs text-white/50">This may take a few seconds on mobile networks</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400">
        {error}
      </div>
    );
  }

  if (!stripePromise) {
    return (
      <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400">
        Stripe is not configured. Please check your backend connection.
      </div>
    );
  }

  return (
    <div className="w-full" id="embedded-checkout">
      <Elements
        stripe={stripePromise}
        options={{
          clientSecret,
          appearance: {
            theme: 'night',
            variables: {
              colorPrimary: '#ffffff',
              colorBackground: '#000000',
              colorText: '#ffffff',
              colorDanger: '#ef4444',
              fontFamily: 'system-ui, sans-serif',
              spacingUnit: '4px',
              borderRadius: '8px',
            },
          },
        }}
      >
        <StripeCheckoutForm 
          clientSecret={clientSecret}
          customerId={customerId}
          planId={planId}
          billingPeriod={billingPeriod}
          userId={userId}
          onComplete={onComplete} 
          onError={onError} 
        />
      </Elements>
    </div>
  );
}
